import { useEffect, useState } from 'react'
import { ContentView, EnrichedContent, TranslatedTextView } from '../../../api/client'
import { useRecoilState } from 'recoil'
import { translateAllState, targetLanguageState } from '../../../store/UIStore'
import mixpanel from 'mixpanel-browser'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import { getSocialMediaContentLink, getSocialMediaPostLink } from './ContentLink'
import { getPlatformDisplayName } from '../../../util/types'
import Spinner from '../../Spinner'

type Props = {
    content: ContentView
    detectedLanguage: string | null
    translatedText?: TranslatedTextView
    translating: boolean
    onTranslateClick: () => void
    enrichedContent?: EnrichedContent
}

const NO_CONTENT_PLACEHOLDER = '[This message has no content]'

export default function ContentText(props: Props) {
    const { content, detectedLanguage, translatedText, translating, onTranslateClick, enrichedContent } = props
    const [translateAll] = useRecoilState(translateAllState)
    const [targetLanguage] = useRecoilState(targetLanguageState)
    const [toggleTranslation, setToggleTranslation] = useState(false)

    useEffect(() => {
        setToggleTranslation(false)
    }, [content.id])

    // Show translate button if detected language differs from target language.
    // IMPORTANT: detectedLanguage comes from lingua's Language::to_string() in Rust
    // (e.g. "English", "Spanish") and targetLanguage comes from LanguageSelectDropDown.
    // Both must use identical casing/spelling for this comparison to work correctly.
    const offerTranslation = !!detectedLanguage && detectedLanguage !== targetLanguage

    /**
     * TRANSLATION LOGIC
     *
     * Only switch to translated view when translation is applicable (language differs).
     * Without the offerTranslation guard, translateAll would hide original content
     * even when source === target language, showing empty space.
     *
     * translateAll       0  0  1  1
     * toggleTranslation  0  1  0  1
     * translate          0  1  1  0
     * This is XOR logic, gated by offerTranslation.
     */
    const translate = offerTranslation && ((!toggleTranslation && translateAll) || (toggleTranslation && !translateAll))

    // Determine button text based on whether we have a translation to show
    const hasTranslation = !!translatedText?.translatedText
    let toggleTranslationText = translating && !hasTranslation
        ? 'Translating...'
        : translate && hasTranslation
          ? `See original in ${detectedLanguage}`
          : 'See translation'

    const showTranslationLoadingSkeleton = translating && !hasTranslation

    function generateOfferTranslation() {
        if (!offerTranslation) {
            return null
        }

        return (
            <span
                className="text-arwen-700 w-fit cursor-pointer text-sm"
                onClick={() => {
                    // If we don't have a translation yet, fetch it
                    if (!hasTranslation) {
                        onTranslateClick()
                    }
                    mixpanel.track(
                        `Translation - single - toggled ${!toggleTranslation ? 'on' : 'off'}`
                    )
                    setToggleTranslation(!toggleTranslation)
                }}
            >
                {toggleTranslationText}
            </span>
        )
    }

    /**
     * When content arrives without text (e.g., GIF-only Instagram comments), the backend stores
     * "[This message has no content]" as a placeholder. Instead of showing this technical string,
     * we display a user-friendly message with a link to view the content on the original platform.
     *
     * This helps moderators understand why content appears empty while giving them a way to
     * see the actual media (GIF/image) on the platform itself.
     */
    const isNoContentPlaceholder = content.content === NO_CONTENT_PLACEHOLDER
    const platformName = getPlatformDisplayName(content.socialMediaServiceName)

    // Try to get a link to the content - prefer direct content link, fall back to post link
    const contentLink =
        content.socialMediaContentLink ||
        getSocialMediaContentLink(enrichedContent, content) ||
        content.socialMediaPostLink ||
        getSocialMediaPostLink(enrichedContent, content)

    const contentToDisplay = isNoContentPlaceholder ? (
        <span className="italic text-ink-600">
            Content not provided by {platformName}.
            {contentLink && (
                <>
                    {' '}
                    <a
                        href={contentLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-arwen-700 hover:text-arwen-800 inline-flex items-center"
                        onClick={() => {
                            mixpanel.track('Content - no content link clicked', {
                                platform: platformName,
                                link: contentLink
                            })
                        }}
                    >
                        View on {platformName}
                        <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
                    </a>
                </>
            )}
        </span>
    ) : (
        <span>{content.content}</span>
    )

    return (
        <div className="flex flex-col ">
            <div className="grid grid-flow-row grid-cols-1 text-ink-900">
                {/* NORMAL CONTENT */}

                <div
                    style={{
                        gridColumnStart: 1,
                        gridRowStart: 1
                    }}
                    className={
                        ' transition-opacity duration-300 ' +
                        (translate ? ' z-1 opacity-0 pointer-events-none' : ' z-2 opacity-100')
                    }
                >
                    {contentToDisplay}
                    {/* {generateOfferTranslation()} */}
                </div>

                {/* TRANSLATED CONTENT */}
                <div
                    style={{
                        gridColumnStart: 1,
                        gridRowStart: 1
                    }}
                    className={
                        ' transition-opacity duration-300 opacity-0' +
                        (translate ? ' z-2 opacity-100' : ' z-1 opacity-0 pointer-events-none')
                    }
                >
                    {showTranslationLoadingSkeleton ? (
                        <span className="inline-flex items-center gap-1.5 text-ink-500 leading-normal">
                            <Spinner size={3} />
                            <span className="text-sm">Translating...</span>
                        </span>
                    ) : (
                        <span>{translatedText?.translatedText}</span>
                    )}

                    {/* {generateOfferTranslation()} */}
                </div>

                {generateOfferTranslation()}
            </div>
        </div>
    )
}
